import React from 'react';

import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { UsersIcon, CupFilledIcon, ClockFilledIcon, DiamondIcon, InfoIcon } from '@icons';
import gtmTracker from '@utils/gtmTracker';

import { useModals } from '@shared/contexts/modal-context';

import prizeGray from '@assets/images/coin-race/race/prize-gray.png';
import prize from '@assets/images/coin-race/race/prize.png';

import { useMutationJoinCoinRace } from '@hooks/api/coin-races';
import { useQueryGetMe } from '@hooks/api/user/use-query-me';

import { CoinRaceType } from '@types';

import { Button } from '@components/button';
import { ModalName } from '@components/modals/types';

import * as Styled from './race-card.styled';
import { TimerBlock } from './timer-block/timer-block';

type RaceCardProps = {
	race: CoinRaceType;
	gradient: string;
	clockColor: string;
};

const formatDate = (dateString: string): string => {
	const date = new Date(dateString);
	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();
	return `${day}.${month}.${year}`;
};

const formatDuration = (seconds: number, t: TFunction): string => {
	const hours = seconds / 3600;
	const days = seconds / (3600 * 24);
	const weeks = seconds / (3600 * 24 * 7);

	if (weeks >= 1) {
		return `${Math.floor(weeks)} ${t('weeks.short')}`;
	} else if (days >= 1) {
		return `${Math.floor(days)} ${t('days.short')}`;
	} else {
		return `${Math.floor(hours)} ${t('hours.short')}`;
	}
};

const getStatusText = (status: string) => {
	if (status === 'active') return 'in_progress_m';
	if (status === 'pending') return 'starting_soon';
	if (status === 'completed') return 'finished_f';
	return status;
};

const RaceCard = ({ race, gradient, clockColor }: RaceCardProps) => {
	const { t } = useTranslation('coin_race');
	const navigate = useNavigate();
	const { openModal } = useModals();
	const joinMutation = useMutationJoinCoinRace();
	const { data: user } = useQueryGetMe();
	const isAuthenticated = !!user?.pm_id;
	const isPending = race.status === 'pending';
	const isCompleted = race.status === 'completed';
	const isJackpot = race.reward_type === 'jackpot_prize';
	const timerDate = isPending ? race.start_date : race.end_date;
	const prizeImage = isCompleted ? prizeGray : prize;

	const durationText = formatDuration(race.duration_seconds, t);

	const handleTrackEvent = (label: string) => {
		gtmTracker.trackEvent('click', {
			event_category: 'Coin_race',
			event_action: 'Click',
			event_label: label
		});
	};

	const handleDetails = () => {
		handleTrackEvent('Details');
		navigate(`/coin-race/${race.id}`);
	};

	const handleJoinClick = () => {
		handleTrackEvent('Join');

		if (!isAuthenticated) {
			openModal(ModalName.LOGIN);
			return;
		}

		joinMutation.mutate(
			{ id: race.id },
			{
				onSuccess: () => {
					openModal(ModalName.JOIN_RACE_CONFIRM, {
						raceName: race.name
					});
				},
				onError: error => {
					console.error('Failed to join race:', error);
				}
			}
		);
	};

	const handleJackpotInfoClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		handleTrackEvent('Jackpot Info');
		openModal(ModalName.JACKPOT_INFO);
	};

	const isDetailsButtonPrimary = race.subscribed || isCompleted || isPending;

	return (
		<Styled.RaceCard $gradient={gradient} $isCompleted={isCompleted} $isPending={isPending} $isJackpot={isJackpot}>
			{(isPending || isCompleted) && (
				<Styled.DateBadge>{formatDate(isPending ? race.start_date : race.end_date)}</Styled.DateBadge>
			)}

			<Styled.StatusBadge $media='desktop' $status={race.status}>
				{t(getStatusText(race.status))}
			</Styled.StatusBadge>

			<Styled.RaceImage>
				<img src={prizeImage} alt={race.name} />
			</Styled.RaceImage>

			<Styled.RaceInfo>
				<Styled.StatusBadge $media='mobile' $isRelative $status={race.status}>
					{t(getStatusText(race.status))}
				</Styled.StatusBadge>

				<Styled.RaceName>{race.name}</Styled.RaceName>

				<TimerBlock.Mobile isCompleted={isCompleted} timerDate={timerDate} />

				{race.reward_type === 'jackpot_prize' && (
					<>
						<Styled.JackpotLabel $alignCenter={isCompleted}>
							<div />
							<Styled.JackpotLabelInfo>
								<DiamondIcon />
								<Styled.JackpotText>JACKPOT</Styled.JackpotText>
							</Styled.JackpotLabelInfo>
							{!isCompleted && (
								<Styled.JackpotLabelInfoIcon onClick={handleJackpotInfoClick}>
									<InfoIcon />
								</Styled.JackpotLabelInfoIcon>
							)}
						</Styled.JackpotLabel>
						<Styled.JackpotValue>
							<Styled.JackpotIcon>
								<DiamondIcon />
							</Styled.JackpotIcon>
							{race.jackpot_points}
						</Styled.JackpotValue>
					</>
				)}
			</Styled.RaceInfo>

			<Styled.RaceStats $isPending={isPending}>
				{!isPending && (
					<Styled.RaceStat>
						<UsersIcon />
						<span>{race.subscriptions_count}</span>
					</Styled.RaceStat>
				)}
				<Styled.RaceStat>
					<CupFilledIcon />
					<span>{race.prizes_count}</span>
				</Styled.RaceStat>
				<Styled.RaceStat $iconColor={clockColor}>
					<ClockFilledIcon />
					<span>{durationText}</span>
				</Styled.RaceStat>
			</Styled.RaceStats>

			<TimerBlock isCompleted={isCompleted} isPending={isPending} timerDate={timerDate} />

			<Styled.RaceActions>
				<Button color={isDetailsButtonPrimary ? 'primary' : undefined} onClick={handleDetails}>
					{t('details')}
				</Button>
				{!isCompleted && !race.subscribed && race.can_subscribe && (
					<Button color='primary' onClick={handleJoinClick} disabled={joinMutation.isPending}>
						{t('join')}
					</Button>
				)}
			</Styled.RaceActions>
		</Styled.RaceCard>
	);
};

export default RaceCard;
